<template>
  <div class="page parcel-detail">
    <div v-if="parcel">
      <div class="container">
        <parcel-geo :parcel="parcel"></parcel-geo>
        <h1 class="has-border-bottom py-3">{{ parcel.parcel.location }}</h1>
        <div class="parcel-data py-3 has-border-bottom">
          <div>
            <label>{{ $t("page.labels.status") }}:</label>
            <span class="is-size-6 has-text-primary has-text-weight-semibold">
              {{ parcel.service_request.super_status_label }}
            </span>
            <span class="is-size-8" v-if="parcel.get_status_display">
              |
              {{ parcel.get_status_display }}
            </span>
          </div>
          <div>
            <label>{{ $t("page.labels.n_cat") }}:</label>
            <span>{{ parcel.parcel.cadastral_ref }}</span>
          </div>
          <div>
            <label>{{ $t("page.labels.municipality") }}:</label>
            <span>{{ parcel.parcel.municipality_name }}</span>
          </div>
          <div>
            <label>{{ $t("page.labels.location") }}:</label>
            <span>{{ parcel.parcel.analysis_with_labels["Parroquia"] }}</span>
          </div>
          <div>
            <label>{{ $t("page.labels.district") }}:</label>
            <span>{{ parcel.parcel.analysis_with_labels["Distrito"] }}</span>
          </div>
          <div>
            <label>{{ $t("page.labels.tecor_holder") }}:</label>
            <span>{{ parcel.parcel.analysis_with_labels["Titulares TECOR"] }}</span>
          </div>
        </div>

        <div v-if="hasSilviculturalModels()">
          <h2 class="has-text-uppercase mt-4 mb-3">
            {{ $t("page.headers.silvicultural_models") }}
          </h2>
          <div class="column">
            <div class="columns is-mobile is-multiline">
              <stand-card
                v-for="(stand, index) in parcel.include_stands"
                :key="stand.id"
                :stand="stand"
                :index="index + 1"
                :total="parcel.include_stands.length"
              />
              <stand-card v-if="parcel.include_stands.length == 0" :stand="parcel" />
            </div>
          </div>
        </div>

        <card css-class="has-background-grey-light" img-class="is-2 pr-3">
          <img slot="image" src="@/assets/parcel.png" />
          <div slot="header">
            <h2 class="has-text-uppercase">{{ $t("page.headers.hectares") }}</h2>
            <h4 class="is-size-2 has-text-primary">
              {{ parcel.parcel.area | areaToHA }}
            </h4>
          </div>
          <div slot="body" class="px-2 pb-2">
            <p>
              <label>{{ $t("page.labels.average_height") }}:</label>
              <span>{{ parcel.parcel.analysis_with_labels["Altitud media"] }}</span>
            </p>
            <p>
              <label>{{ $t("page.labels.average_slope") }}:</label>
              <span>{{ parcel.parcel.analysis_with_labels["Pendiente media"] }}</span>
            </p>
            <p>
              <label>{{ $t("page.labels.dominant_exposure") }}:</label>
              <span>
                {{ parcel.parcel.analysis_with_labels["Exposición dominante"] }}
              </span>
            </p>
            <p>
              <label>{{ $t("page.labels.degree_of_actual_erosion") }}:</label>
              <span>
                {{ parcel.parcel.analysis_with_labels["Grado de erosión real"] }}
              </span>
            </p>
          </div>
        </card>
        <h2 class="has-text-uppercase has-text-primary mt-5">
          {{ $t("page.headers.affectations") }}
        </h2>
        <ul class="mt-2" v-if="affectations.length">
          <li class="is-flex" v-for="(affectation, key) in affectations" :key="key">
            <article :class="['collapsible-article', { active: affectation.expanded }]">
              <header
                class="header background-gray"
                @click="affectation.expanded = !affectation.expanded"
              >
                <div
                  class="article-icon has-text-centered is-size-1 has-text-grey"
                  :class="affectation.icon"
                ></div>
                <h1 class="py-2 pl-2 pr-1">{{ affectation.article.title }}</h1>
                <div class="arrow"></div>
              </header>
              <div class="body pa-2" v-if="affectation.expanded">
                <div v-html="$options.filters.nl2p(affectation.article.body)" />
                <div v-if="affectation.article.attachments">
                  <div
                    class="attachment"
                    v-for="(filename, index) in affectation.article.attachments.split(
                      ' '
                    )"
                    :key="index"
                  >
                    <img
                      @click="windowOpen(docsResource(filename))"
                      :src="docsResource(filename)"
                    />
                  </div>
                </div>
              </div>
            </article>
          </li>
        </ul>

        <h2 class="has-text-uppercase has-text-primary mt-5">
          {{ $t("page.headers.tracing") }}
        </h2>

        <ul class="mt-2" v-if="tracing.length">
          <li class="is-flex" v-for="(element, key) in tracing" :key="key">
            <visit-card
              v-if="element.entity_class == 'Visit'"
              :element="element"
            ></visit-card>
            <incidence-card
              v-if="element.entity_class == 'Incidence'"
              :element="element"
            ></incidence-card>
          </li>
        </ul>

        <p v-else class="is-size-6 py-2">{{ $t("page.content.no_affectations") }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import { mapMutations } from "vuex"
import BikentapediaMixin from "@/mixins/BikentapediaMixin"
import Card from "@/components/Card"
import IncidenceCard from "@/components/IncidenceCard"
import ParcelGeo from "@/components/ParcelGeo"
import StandCard from "@/components/StandCard"
import VisitCard from "@/components/VisitCard"
import _ from "lodash"

export default {
  name: "parcel-detail",
  components: { Card, IncidenceCard, ParcelGeo, StandCard, VisitCard },
  mixins: [BikentapediaMixin],
  mounted() {
    if (!this.pk) return
    this.getParcel()
  },
  computed: {
    pk() {
      return this.$route.params.id
    },
  },
  data() {
    return {
      parcel: null,
      attachments: [],
      affectations: [],
      tracing: [],
    }
  },
  methods: {
    ...mapMutations(["windowOpen"]),
    getAffectations() {
      let affectationMapping = {
        fluvial: { icon: "ico-fluvial", article: "1" },
        "espacio-natural": { icon: "ico-espacio-natural", article: "2" },
        "especie-amenazada": { icon: "ico-especie-amenazada", article: "3" },
        incendios: { icon: "ico-incendios", article: "4" },
        patrimonio: { icon: "ico-patrimonio", article: "5" },
      }

      this.affectations = this.parcel.parcel.affectations.map((key) => {
        return {
          icon: affectationMapping[key].icon,
          article: this.articles[affectationMapping[key].article],
          expanded: false,
        }
      })
    },
    getParcel() {
      this.$store.dispatch("loadParcel", this.pk).then(() => {
        this.parcel = this.$store.state.parcel
        this.getAffectations()
        this.getTracing()
      })
    },
    hasSilviculturalModels() {
      const silviculturalModels = _.map(
        this.parcel.include_stands,
        "silvicultural_model"
      )
      const parcelSM = _.get(this.parcel, "silvicultural_model", null)
      silviculturalModels.push(parcelSM)
      return _.some(silviculturalModels)
    },
    getTracing() {
      api.getParcelTracing(this.pk).then((response) => {
        response.data.forEach((element) => {
          element.expanded = false
        })

        this.tracing = response.data
      })
    },
  },
}
</script>
